import {
  HIRER_AD_PURCHASER_FIRST_TIME,
  HIRER_AD_PURCHASER_REPEAT,
} from 'src/app/shared/constants/tealium';
import type { ProductList } from 'src/app/shared/hooks/useProductSetFromCart';
import type { ShoppingCartContextProps } from 'src/app/shared/hooks/useShoppingCart';
import { getAdProductFromCart } from 'src/app/utils/getAdProductFromCart';
import { getContractType } from 'src/app/utils/getContractType';
import { getPricingType } from 'src/app/utils/getPricingType';
import {
  BillingStatus,
  type Cart,
  type DraftJob,
  type HirerAccountAndUserDataQuery,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

type ProductAttributes = Partial<
  Pick<
    AppState['tealium'],
    | 'orderNumber'
    | 'productSetId'
    | 'jobAdTypeSelected'
    | 'jobAdTypePosted'
    | 'addOnAdProductNames'
  >
>;

export const deriveTrackingAttributes = (
  draft: DraftJob,
  hirerData: HirerAccountAndUserDataQuery,
  page: string,
  cart?: Cart,
  productSet?: ProductList | null,
  order?: ShoppingCartContextProps['orderState'],
): Partial<AppState['tealium']> => {
  const { hirerAccount, contractBalances, currentUser, identity } = hirerData;
  let productRelatedAttributes: ProductAttributes = {};

  if (!hirerAccount) return {};

  if (productSet && cart) {
    const parentProduct = getAdProductFromCart(
      cart.items,
      productSet.items,
      'AdProduct',
    )?.[0];

    const addOns = getAdProductFromCart(
      cart.items,
      productSet.items,
      'AdProductAddOn',
    );

    productRelatedAttributes = {
      productSetId: cart.productSetId,
      jobAdTypeSelected: parentProduct?.displayMetadata?.name?.toLowerCase(),
      jobAdTypePosted: parentProduct?.displayMetadata?.name?.toLowerCase(),
      addOnAdProductNames: addOns.map((addOn) => addOn.name.toLowerCase()),
      orderNumber: order?.order?.orderNumber,
    };
  }

  return {
    advertiser: {
      type: getContractType(contractBalances),
      pricingType: getPricingType(hirerAccount.pricingModel),
    },
    draftId: draft.id,
    hirerContract: {
      adType: contractBalances[0]?.adType?.toLowerCase(),
      value:
        Number(contractBalances[0]?.totalValue?.dollars?.original?.value) / 100,
      balance:
        Number(contractBalances[0]?.totalValue?.dollars?.remaining?.value) /
        100,
      type: contractBalances[0]?.contractType?.toLowerCase(),
    },
    hirerInfo:
      currentUser.__typename === 'CurrentUserContext'
        ? {
            firstName: currentUser?.firstName,
            lastName: currentUser?.lastName,
            emailAddress: currentUser?.email,
          }
        : {},
    seekAdvertiserId: hirerAccount.id,
    userId: identity.subject.userId,
    page,
    country: hirerAccount.nation?.toLowerCase(),
    hirerAdPurchaser:
      hirerAccount.isApproved === false
        ? HIRER_AD_PURCHASER_FIRST_TIME
        : HIRER_AD_PURCHASER_REPEAT,
    isOnCreditHold: hirerAccount.billingStatus === BillingStatus.ON_CREDIT_HOLD,
    priceVisibility: hirerAccount.hidePrice ? 'hidden' : 'shown',
    ...productRelatedAttributes,
  };
};
